<template>
  <!-- tutorial-callout: the house auto-update already waits for that class (utils/appUpdate.js). -->
  <div v-if="tut && step" class="tut tutorial-callout" :style="{ '--role': colour }">
    <!-- Always on top while it runs: whose practice this is, and the way out. -->
    <div class="tut__band">
      <span class="tut__band-label">Tutorial · nothing here counts</span>
      <button class="tut__leave" @click="leave">Leave</button>
    </div>

    <!-- The plain cards: the opening, the role, the choices, the end. -->
    <div v-if="step.kind === 'card'" class="tut__veil">
      <div class="tut__card" role="dialog" aria-modal="true" :aria-label="step.title">
        <span class="tut__count">{{ index + 1 }} of {{ steps.length }}</span>
        <h3 class="tut__title">{{ step.title }}</h3>
        <!-- eslint-disable vue/no-v-html -- our own copy, through rich() -->
        <p v-if="step.text" class="tut__text" v-html="rich(step.text)" />
        <p v-if="step.why" class="tut__why" v-html="rich(step.why)" />
        <dl v-if="list" class="tut__list">
          <template v-for="[term, line] in list" :key="term">
            <dt>{{ term }}</dt>
            <dd v-html="rich(line)" />
          </template>
        </dl>
        <p v-if="step.foot" class="tut__foot">{{ step.foot }}</p>
        <div class="tut__foot-row">
          <button v-if="index > 0" class="linkish tut__back" @click="back">Back</button>
          <button class="primary tut__go" @click="advance">{{ step.next || 'Next' }}</button>
        </div>
      </div>
    </div>

    <!-- The coach: a hole in the dark over the real board, and one line. -->
    <template v-else-if="!blocked">
      <div v-if="rect" class="tut__spot" :style="spotStyle" aria-hidden="true" />
      <div class="tut__coach" :class="{ 'tut__coach--top': coachOnTop }" role="status">
        <span class="tut__count">{{ index + 1 }} of {{ steps.length }}</span>
        <p class="tut__text" v-html="rich(missing && step.needs ? step.needs.text : (shown && step.then ? step.then.text : step.text))" />
        <!-- eslint-enable vue/no-v-html -->
        <div class="tut__coach-foot">
          <button v-if="index > 0" class="linkish tut__back" @click="back">Back</button>
          <!-- A step come back to for a reread has Next, whatever it waits for. -->
          <button v-if="step.next || revisiting" class="primary tut__go" @click="advance">{{ step.next || 'Next' }}</button>
          <template v-else>
            <span class="tut__waiting">Your turn</span>
            <button v-if="step.skip" class="linkish tut__skip" @click="advance">{{ step.skip }}</button>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { TUTORIALS } from '@/tutorial/scripts';
import { roleOfTutorial } from '@/tutorial/sandbox';
import { ROLE_INFO } from '@/engine/constants';
import { logTotal, rolesOf } from '@/engine/game';
import { artForResource } from '@/components/game/resourceArt';

// The tutorial's one moving part. It knows nothing about the boards: it
// lights up whatever its step names and waits for the practice game (or the
// screen) to show the thing it asked for. See src/tutorial/scripts.js.
const PAD = 8;

export default {
  name: 'TutorialCoach',
  data() {
    return {
      rect: null,
      blocked: false,
      // The step's `needs` is not on screen (a card put back down).
      missing: false,
      // The step's `then` is on screen (half done: say the second half).
      shown: false,
      // Where the practice log stood when this step began.
      mark: 0,
      // Stepped back to reread (Matt, playtest 10: "I just did something
      // without really reading what it said"): the step gets a Next button,
      // whatever it normally waits for, so a done thing is not asked again.
      revisiting: false,
      timer: null,
    };
  },
  computed: {
    ...mapState(['tutorial', 'game']),
    tut() { return this.tutorial ? TUTORIALS[this.tutorial.role] : null; },
    steps() { return this.tut?.steps || []; },
    index() { return this.tutorial?.step || 0; },
    step() { return this.steps[this.index] || null; },
    colour() { return ROLE_INFO[this.playRole]?.colour; },
    // The role the practice is held as (the machines are the Scholars').
    playRole() { return roleOfTutorial(this.tutorial?.role); },
    // The roles this phone holds in the real game (stashed under the practice one).
    list() {
      if (!this.step?.listFor) return this.step?.list;
      return this.step.listFor(rolesOf(this.tutorial.stash, this.$store.getters.myPlayerId));
    },
    // The line goes at the top when what it points at is low on the screen.
    coachOnTop() {
      if (!this.rect) return false;
      return this.rect.top + this.rect.height / 2 > window.innerHeight * 0.55;
    },
    spotStyle() {
      const r = this.rect;
      return {
        top: `${r.top - PAD}px`, left: `${r.left - PAD}px`, width: `${r.width + PAD * 2}px`, height: `${r.height + PAD * 2}px`,
      };
    },
    logCount() { return logTotal(this.game); },
  },
  watch: {
    index: { handler() { this.begin(); }, immediate: true },
    tutorial(now, was) { if (now && !was) this.begin(); },
    logCount() { this.checkAct(); },
  },
  // The band sits over the top of the screen, so the board moves down under
  // it while the tutorial runs - it was hiding the purse strip, which is
  // where a role's workers (and the Stewards' shifts) live (playtest 11).
  mounted() {
    this.timer = setInterval(this.tick, 200);
    document.documentElement.classList.add('tutorial-on');
  },
  beforeUnmount() {
    clearInterval(this.timer);
    document.documentElement.classList.remove('tutorial-on');
  },
  methods: {
    begin() {
      this.mark = logTotal(this.game);
      this.rect = null;
      this.$nextTick(() => {
        this.tick();
        const el = this.target();
        el?.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        // Never sideways: a swipe pager must stay where its own transform
        // puts it (older browsers without overflow: clip).
        setTimeout(() => document.querySelectorAll('.pager').forEach((p) => { p.scrollLeft = 0; }), 400);
      });
    },
    // A target may name several things (a comma list): the spotlight takes
    // in all of them that are on screen - the Scholars' flasks AND the
    // picker that opens under them, which sat in the dark and read as
    // switched off (the learner, 2026-10-12).
    target() {
      const sel = this.missing && this.step?.needs ? this.step.needs.target : (this.shown && this.step?.then ? this.step.then.target : this.step?.target);
      if (!sel) return null;
      let els = [];
      try { els = [...document.querySelectorAll(sel)]; } catch { return null; }
      if (els.length <= 1) return els[0] || null;
      const rs = els.map((e) => e.getBoundingClientRect()).filter((r) => r.width && r.height);
      if (!rs.length) return els[0];
      const top = Math.min(...rs.map((r) => r.top)); const left = Math.min(...rs.map((r) => r.left));
      const bottom = Math.max(...rs.map((r) => r.bottom)); const right = Math.max(...rs.map((r) => r.right));
      return { getBoundingClientRect: () => ({ top, left, width: right - left, height: bottom - top }), scrollIntoView: (o) => els[0].scrollIntoView?.(o) };
    },
    // Follow the target as the page moves; step on when the screen shows
    // what the step asked for; stand aside while a dialog is up (the
    // outcome card, the confirm) so it can be read and closed.
    tick() {
      if (!this.step) return;
      this.blocked = Boolean(document.querySelector('.howto-backdrop'));
      // A step that relies on something staying put (a card in hand) points
      // back at it when it is gone, instead of asking for the impossible.
      this.missing = Boolean(this.step.needs && !document.querySelector(this.step.needs.dom));
      // And one that comes in two halves says the second once the first is done.
      this.shown = Boolean(!this.missing && this.step.then && document.querySelector(this.step.then.dom));
      const el = this.target();
      if (el) {
        const b = el.getBoundingClientRect();
        if (!this.rect || b.top !== this.rect.top || b.left !== this.rect.left || b.width !== this.rect.width || b.height !== this.rect.height) {
          this.rect = {
            top: b.top, left: b.left, width: b.width, height: b.height,
          };
        }
      } else if (this.rect) {
        this.rect = null;
      }
      if (this.step.waitDom && !this.blocked && document.querySelector(this.step.waitDom)) this.advance();
      // A step asking for a tap on something this screen hides has nothing
      // to ask: on a tablet the swap tabs go, because both cards show side
      // by side. Playtest OzC8Y ("there isn't a second tab").
      else if (this.step.waitDom && !this.blocked && this.hiddenHere(this.step.target)) this.advance();
      // Or for the practice game itself to show it (a worker given at dawn).
      if (this.step.waitGame && this.game && this.step.waitGame(this.game, this.playRole)) this.advance();
    },
    hiddenHere(sel) {
      if (!sel) return false;
      let el = null;
      try { el = document.querySelector(sel); } catch { return false; }
      return Boolean(el && getComputedStyle(el.closest('.swap__tabs') || el).display === 'none');
    },
    checkAct() {
      const want = this.step?.waitAct;
      if (!want || !this.game) return;
      const base = this.game.logBase || 0;
      const fresh = (this.game.log || []).slice(Math.max(0, this.mark - base));
      if (fresh.some((l) => l.kind === 'act' && l.role === this.playRole && l.action === want)) this.advance();
    },
    advance() {
      this.revisiting = false;
      if (this.index >= this.steps.length - 1) { this.leave(); return; }
      this.$store.commit('setTutorialStep', this.index + 1);
    },
    // Back over any step the screen would step straight past again (a tab
    // already on, the day already started) to the last one worth rereading.
    back() {
      let i = this.index - 1;
      const past = (s) => (s.waitDom && document.querySelector(s.waitDom)) || (s.waitGame && this.game && s.waitGame(this.game, this.playRole));
      while (i > 0 && past(this.steps[i])) i -= 1;
      this.revisiting = true;
      this.$store.commit('setTutorialStep', i);
    },
    // Our own copy, with two marks: *word* for a button or action name, and
    // {workers} for the worker glyph the boards price things in (Matt,
    // playtest 10: say "costs one worker" and show the icon beside it).
    rich(text) {
      const safe = String(text || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      return safe
        .replace(/\*([^*]+)\*/g, '<b>$1</b>')
        .replace(/\{(workers|food|goods|coin|knowledge)\}/g, (_, k) => `<span class="pip pip--${k} tut__pip"><svg class="pip__icon" viewBox="0 0 16 16" aria-hidden="true">${artForResource(k)}</svg></span>`);
    },
    leave() {
      try { localStorage.setItem(`commonwealth:tutorial-done:${this.tutorial.role}`, '1'); } catch { /* storage off */ }
      this.$store.dispatch('endTutorial');
    },
  },
};
</script>

<style lang="scss" scoped>
.tut__band {
  position: fixed; z-index: 47; top: 0; left: 0; right: 0;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: calc(env(safe-area-inset-top, 0px) + 4px) 12px 4px;
  min-height: calc(env(safe-area-inset-top, 0px) + 40px); box-sizing: border-box;
  background: var(--role, #d9a441); color: #111;
  font-size: 0.78rem; font-weight: 700; letter-spacing: 0.02em;
}
.tut__leave {
  background: rgba(0, 0, 0, 0.18); color: #111; border: 0; border-radius: 999px;
  padding: 3px 12px; font-weight: 700; font-size: 0.78rem; cursor: pointer;
}
.tut__veil {
  position: fixed; inset: 0; z-index: 46;
  display: grid; place-items: center; padding: 16px;
  background: rgba(0, 0, 0, 0.7);
}
.tut__card {
  width: min(440px, 100%);
  background: #14181f; color: #eef1f5;
  border: 2px solid var(--role, #d9a441); border-radius: 14px;
  padding: 18px 18px 16px;
  display: flex; flex-direction: column; gap: 10px;
}
.tut__count { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--role, #d9a441); font-weight: 700; }
.tut__title { margin: 0; font-size: 1.3rem; line-height: 1.2; }
.tut__text { margin: 0; font-size: 1rem; line-height: 1.45; }
.tut__why { margin: 0; font-size: 0.95rem; line-height: 1.45; color: #c9d1db; }
.tut__foot { margin: 0; font-size: 0.85rem; color: #9aa6b4; }
.tut__list {
  margin: 0; display: grid; grid-template-columns: auto 1fr; gap: 8px 12px; align-items: baseline;
  dt { font-weight: 800; color: var(--role, #d9a441); white-space: nowrap; }
  dd { margin: 0; line-height: 1.4; }
}
.tut__go { align-self: flex-end; }
.tut__foot-row { display: flex; align-items: center; justify-content: flex-end; gap: 14px; }
.tut__back { font-size: 0.85rem; color: #9aa6b4; margin-right: auto; }
.tut__coach-foot .tut__back { margin-right: 0; }
.tut__text :deep(b) { color: var(--role, #d9a441); }
.tut__text :deep(.tut__pip) { vertical-align: -2px; }
.tut__spot {
  position: fixed; z-index: 45; pointer-events: none;
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
  outline: 3px solid var(--role, #d9a441);
  animation: tut-pulse 1.2s ease-in-out infinite;
  transition: top 0.15s, left 0.15s, width 0.15s, height 0.15s;
}
@keyframes tut-pulse {
  0%, 100% { outline-offset: 0; outline-color: var(--role, #d9a441); }
  50% { outline-offset: 5px; outline-color: #fff; }
}
@media (prefers-reduced-motion: reduce) {
  .tut__spot { animation: none; }
}
.tut__coach {
  position: fixed; z-index: 46; left: 50%; transform: translateX(-50%);
  bottom: calc(var(--safe-bottom, 0px) + 84px);
  width: min(460px, calc(100% - 24px));
  background: #14181f; color: #eef1f5;
  border: 2px solid var(--role, #d9a441); border-radius: 12px;
  padding: 10px 14px 10px;
  display: flex; flex-direction: column; gap: 6px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.5);
}
.tut__coach--top { bottom: auto; top: calc(env(safe-area-inset-top, 0px) + 36px); }
.tut__coach-foot { display: flex; align-items: center; justify-content: flex-end; gap: 14px; }
.tut__waiting { margin-right: auto; font-size: 0.8rem; font-weight: 700; color: var(--role, #d9a441); }
.tut__skip { font-size: 0.85rem; }
.tut__note {
  position: fixed; z-index: 51; left: 50%; transform: translateX(-50%);
  top: calc(env(safe-area-inset-top, 0px) + 36px);
  width: min(460px, calc(100% - 24px));
  pointer-events: none;
  background: #14181f; color: #eef1f5;
  border: 2px solid var(--role, #d9a441); border-radius: 12px;
  padding: 10px 14px; font-size: 0.95rem; line-height: 1.4;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.5);
}
</style>
